import { Modal, Form, Button, Row, Col } from "react-bootstrap";
import { useState } from "react";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "react-query";
import ReactDatePicker from "react-datepicker";
import currency from "currency.js";
import DatePickerCustomInput from "./utils/DatePickerCustomInput";
import { useBackendUrl } from "../utils/hooks";
import { fetchActionsUtil } from "../utils/helpers";

function InfoField({ label, value }) {
  return (
    <Col md={6} className="mb-3">
      <div className="text-muted small mb-1">{label}</div>
      <div className="fw-medium">{value || "—"}</div>
    </Col>
  );
}

export default function DeliveryInfoModal({
  showDeliveryInfoModal,
  setShowDeliveryInfoModal,
  TransactionID,
}) {
  const backendUrl = useBackendUrl();
  const queryClient = useQueryClient();

  const [LoadingDate, setLoadingDate] = useState(null);
  const [DeliveryDate, setDeliveryDate] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null);

  // terms is stored as a JSON-stringified array of { text } lines, e.g.
  // [{"text": "Payment due in 30 days"}] — same shape Cart.jsx writes.
  const termsText = (() => {
    if (!deliveryInfo?.terms) return "";
    try {
      const parsed = JSON.parse(deliveryInfo.terms);
      return (Array.isArray(parsed) ? parsed : [])
        .map(el => el?.text || "")
        .filter(Boolean)
        .join("\n");
    } catch {
      return deliveryInfo.terms;
    }
  })();

  const { isFetching } = useQuery(
    ["DELIVERY_INFO", TransactionID],
    () =>
      fetchActionsUtil(
        `${backendUrl}/api/invoice/delivery-info/${TransactionID}`,
        "GET"
      ),
    {
      enabled: Boolean(showDeliveryInfoModal && TransactionID),
      onSuccess: ({ data }) => {
        setDeliveryInfo(data);
        setLoadingDate(data?.LoadingDate ? new Date(data.LoadingDate) : null);
        setDeliveryDate(
          data?.DeliveryDate ? new Date(data.DeliveryDate) : null
        );
      },
      onError: () => {
        toast.error("Unable to load delivery info for this transaction");
        setShowDeliveryInfoModal(false);
      },
    }
  );

  const updateDeliveryInfoMutation = useMutation(
    () =>
      fetchActionsUtil(
        `${backendUrl}/api/invoice/delivery-info/${TransactionID}`,
        "PUT",
        null,
        { LoadingDate, DeliveryDate }
      ),
    {
      onSuccess: () => {
        toast.success("Delivery info updated");
        queryClient.invalidateQueries(["DELIVERY_INFO", TransactionID]);
        setShowDeliveryInfoModal(false);
      },
      onError: () => {
        toast.error("Unable to update delivery info");
      },
    }
  );

  return (
    <Modal
      show={showDeliveryInfoModal}
      onHide={() => setShowDeliveryInfoModal(false)}
      centered
      animation={false}
      enforceFocus={false}
      backdrop="static"
      size="lg"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title className="h5">Delivery Info</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={isFetching ? { pointerEvents: "none", opacity: 0.5 } : {}}
      >
        <Row>
          <InfoField label="Driver Name" value={deliveryInfo?.DriverName} />
          <InfoField
            label="Driver Phone Number"
            value={deliveryInfo?.DriverPhoneNumber}
          />
          <InfoField label="Truck Number" value={deliveryInfo?.TruckNumber} />
          <InfoField label="Warehouse" value={deliveryInfo?.Warehouse} />
          <InfoField label="Delivery" value={deliveryInfo?.Delivery} />
          <InfoField label="Region" value={deliveryInfo?.Region} />
          <InfoField
            label="Loading Charge"
            value={currency(deliveryInfo?.LoadingCharge || 0, {
              symbol: "",
            }).format()}
          />
          <InfoField
            label="Offloading Charge"
            value={currency(deliveryInfo?.OffloadingCharge || 0, {
              symbol: "",
            }).format()}
          />
          <InfoField
            label="Transport Charge"
            value={currency(deliveryInfo?.TransportCharge || 0, {
              symbol: "",
            }).format()}
          />
          <InfoField
            label="Pos Charge"
            value={currency(deliveryInfo?.PosCharge || 0, {
              symbol: "",
            }).format()}
          />
          <InfoField
            label="Shipping Cost"
            value={currency(deliveryInfo?.shippingCost || 0, {
              symbol: "",
            }).format()}
          />
          <InfoField
            label="Other Charges"
            value={currency(deliveryInfo?.otherCharges || 0, {
              symbol: "",
            }).format()}
          />
        </Row>

        {termsText ? (
          <div className="mb-3">
            <div className="text-muted small mb-1">Terms</div>
            <div className="fw-medium" style={{ whiteSpace: "pre-wrap" }}>
              {termsText}
            </div>
          </div>
        ) : null}

        <hr />

        <Form.Group className="mb-3">
          <Form.Label className="mb-1">Loading Date</Form.Label>
          <div>
            <ReactDatePicker
              selected={LoadingDate}
              onChange={date => setLoadingDate(date)}
              customInput={<DatePickerCustomInput />}
              dateFormat="MMMM dd, yyyy"
              showPopperArrow={false}
              placeholderText="Select loading date"
              isClearable
            />
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="mb-1">Delivery Date</Form.Label>
          <div>
            <ReactDatePicker
              selected={DeliveryDate}
              onChange={date => setDeliveryDate(date)}
              customInput={<DatePickerCustomInput />}
              dateFormat="MMMM dd, yyyy"
              showPopperArrow={false}
              placeholderText="Select delivery date"
              isClearable
            />
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="white"
          className="border bg-white"
          disabled={updateDeliveryInfoMutation.isLoading}
          onClick={() => setShowDeliveryInfoModal(false)}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={isFetching || updateDeliveryInfoMutation.isLoading}
          onClick={() => updateDeliveryInfoMutation.mutate()}
        >
          {updateDeliveryInfoMutation.isLoading ? "Please wait…" : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
